import React, {
	memo,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import fp from "lodash/fp";
import { Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Field, change } from "redux-form";

import { CustomDropdownByValue } from "components/formFields/dropdown";
import { FeeProgramByValue } from "helpers/enums-helper";
import { useFeeProgramsQuery } from "queries";
import { required } from "validators/validation";

import { errMapper } from "./helpers/errHelpers";
import { flatRateMapper } from "./helpers/flatRateHelpers";
import { interchangePlusMapper } from "./helpers/interchangePlusHelpers";
import { treeTierMapper } from "./helpers/threeTierMapper";
import AdditionalSection from "./tabsChoose/additionalSection";
import DualFlatRate from "./tabsChoose/dualFlatRate";
import ERR from "./tabsChoose/err";
import FlatRate from "./tabsChoose/flatRate";
import InterchangePlus from "./tabsChoose/interchangePlus";
import InterchangePlusAsAdditional from "./tabsChoose/interchangePlusAsAdditional";
import ThreeTier from "./tabsChoose/threeTier";
import FeesTemplateContext from "../feesTemplateContext";

const FeesTabsFields = ({
	formName = "feesTab",
	feeTemplateDropdownEnabled = true,
	feeProgram,
}) => {
	const dispatch = useDispatch();
	const {
		isFetching: feeTemplateLoading,
		data: { feeProgramTemplates: feeTemplate } = {},
		application: { isPaperApplication },
	} = useContext(FeesTemplateContext);

	const { data: feePrograms } = useFeeProgramsQuery();

	const [templateGroup, setTemplateGroup] = useState([]);
	const [valueInField, setCurrentValueInField] = useState(false);

	useEffect(() => {
		if (feeProgram && feeTemplate) {
			const filterFeeTemplate = fp.filter(
				item => item.type === FeeProgramByValue(feeProgram),
				feeTemplate,
			);
			setTemplateGroup(filterFeeTemplate);
		} else if (!feeProgram && feeTemplate) {
			setTemplateGroup(feeTemplate);
		} else if (fp.isEmpty(feeTemplate)) {
			// Reset fee template ddl if no fee templates are available
			setTemplateGroup([]);
		}
	}, [setTemplateGroup, feeTemplate, feeProgram, dispatch, formName]);

	useEffect(() => {
		if (valueInField) {
			dispatch(change(formName, "feeTemplate", ""));
			dispatch(change(formName, "feeTemplateId", ""));
		}
	}, [dispatch, valueInField, formName]);

	const feeTemplateValue = templateGroup.map(data => {
		return {
			value: data.name,
			name: data.name,
			type: `${data.type === "3-Tier" ? "Three Tier" : data.type}`,
		};
	});

	const groupedTemplateItems = fp.groupBy(
		item => item.type,
		feeTemplateValue,
	);

	const handleChangeFeeProgram = () => {
		setCurrentValueInField(false);
		dispatch(change(formName, "feeTemplate", ""));
		dispatch(change(formName, "feeTemplateId", ""));
	};

	const handleChangeFeeTemplate = (_e, templateName) => {
		setCurrentValueInField(false);

		const feeTemplateToApply = fp.find(
			t => t.name === templateName,
			feeTemplate,
		);

		if (feeTemplateToApply?.type === "Interchange Plus") {
			dispatch(change(formName, "feeProgram", "InterchangePlus"));
			interchangePlusMapper(
				formName,
				dispatch,
				change,
				feeTemplateToApply,
			);
		} else if (feeTemplateToApply?.type === "Flat Rate") {
			dispatch(change(formName, "feeProgram", "FlatRate"));
			flatRateMapper(formName, dispatch, change, feeTemplateToApply);
		} else if (feeTemplateToApply?.type === "3-Tier") {
			dispatch(change(formName, "feeProgram", "ThreeTier"));
			treeTierMapper(formName, dispatch, change, feeTemplateToApply);
		} else if (feeTemplateToApply?.type === "ERR") {
			dispatch(change(formName, "feeProgram", "ERR"));
			errMapper(formName, dispatch, change, feeTemplateToApply);
		}
		// Set the fee template id to form field
		dispatch(change(formName, "feeTemplateId", feeTemplateToApply?.id));
	};

	const FeeProgramFields = useCallback(() => {
		switch (feeProgram) {
			case "InterchangePlus":
				return (
					<InterchangePlus
						setCurrentValueInField={setCurrentValueInField}
						isPaperApplication={isPaperApplication}
					/>
				);
			case "FlatRate":
				return (
					<FlatRate
						setCurrentValueInField={setCurrentValueInField}
						isPaperApplication={isPaperApplication}
					/>
				);
			case "ThreeTier":
				return (
					<>
						<ThreeTier
							setCurrentValueInField={setCurrentValueInField}
							isPaperApplication={isPaperApplication}
						/>
						<AdditionalSection
							feeProgramName="Pass Through Interchange"
							feeProgramField={<InterchangePlusAsAdditional />}
							formName={formName}
							sectionName="isPassThroughInterchange"
						/>
					</>
				);
			case "ERR":
				return (
					<ERR
						setCurrentValueInField={setCurrentValueInField}
						isPaperApplication={isPaperApplication}
					/>
				);
			case "DualFlatRate":
				return (
					<DualFlatRate
						setCurrentValueInField={setCurrentValueInField}
						isPaperApplication={isPaperApplication}
					/>
				);
			default:
				return null;
		}
	}, [feeProgram, formName, isPaperApplication]);

	return (
		<>
			<Row>
				<Col md={5}>
					<Field
						component={CustomDropdownByValue}
						label="Fee Program"
						validate={!isPaperApplication ? [required] : []}
						name="feeProgram"
						items={feePrograms}
						loading={feeTemplateLoading}
						onChange={handleChangeFeeProgram}
						hideRequiredMark={isPaperApplication}
					/>
				</Col>
				{feeTemplateDropdownEnabled && (
					<Col md={{ span: 5, offset: 2 }}>
						<Field
							component={CustomDropdownByValue}
							label="Fee Template"
							name="feeTemplate"
							items={groupedTemplateItems}
							loading={feeTemplateLoading}
							onChange={handleChangeFeeTemplate}
							hideRequiredMark
							optgroup
						/>
					</Col>
				)}
			</Row>
			<FeeProgramFields />
		</>
	);
};

const component = memo(FeesTabsFields);
export { component as FeesTabsFields };
